/**
 * Recursively removes all properties with undefined values from an object.
 *
 * @template T
 * @param {T} obj - The object to be sanitized.
 * @returns {T} - A new object with all undefined values removed
 */
export const removeUndefinedDeep = <T>(
  obj: T,
  seen: WeakSet<object> = new WeakSet(),
): T => {
  // Return primitive values as is
  if (obj === undefined || obj === null || typeof obj !== 'object') return obj;

  // Prevent infinite recursion by detecting circular references
  // If object was already seen, return it without further processing
  if (seen.has(obj as object)) return obj;
  seen.add(obj as object);

  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedDeep(item, seen)) as unknown as T;
  }

  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    const value = obj[key];
    if (value !== undefined) {
      if (value && typeof value === 'object') {
        result[key] = removeUndefinedDeep(value, seen) as T[keyof T];
      } else {
        result[key] = value;
      }
    }
  }

  return result as T;
};
